import httpClient from './httpClient';

class TemplateService {
  constructor() {
    this.baseURL = '/api/templates';
  }

  /**
   * List all templates with optional filtering
   */
  async listTemplates(options = {}) {
    try {
      const {
        entityType,
        language,
        page = 1,
        pageSize = 20,
        search
      } = options;

      // Backend expects camelCase query names
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      if (entityType) params.set('entityType', entityType);
      if (language) params.set('language', language);
      if (search && search.trim()) params.set('search', search.trim());

      const response = await httpClient.get(`${this.baseURL}?${params.toString()}`);
      // Unwrap success_response shape { success, data }
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error listing templates:', error);
      throw error;
    }
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(templateId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error getting template:', error);
      throw error;
    }
  }

  /**
   * Create a new template
   */
  async createTemplate(templateData) {
    try {
      const response = await httpClient.post(this.baseURL, templateData);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  /**
   * Upload a template file with metadata
   */
  async uploadTemplate(file, metadata, onProgress = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add metadata fields
      Object.keys(metadata).forEach(key => {
        if (metadata[key] !== undefined && metadata[key] !== null) {
          if (Array.isArray(metadata[key])) {
            formData.append(key, metadata[key].join(','));
          } else {
            formData.append(key, metadata[key].toString());
          }
        }
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minute timeout for file uploads
      };

      // Add progress tracking if callback provided
      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        };
      }

      const response = await httpClient.post(this.baseURL, formData, config);

      return response.data;
    } catch (error) {
      console.error('Error uploading template:', error);
      
      // Enhanced error handling with user-friendly messages
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 400:
            if (data.details && data.details.validation_errors) {
              throw new Error(`模板验证失败：${data.details.validation_errors.join(', ')}`);
            }
            throw new Error(data.error || '模板文件格式或内容有误，请检查后重试');
          
          case 409:
            throw new Error('模板ID已存在，请使用不同的模板ID');
          
          case 413:
            throw new Error('文件过大，请选择小于10MB的文件');
          
          case 415:
            throw new Error('不支持的文件格式，请选择.xlsx或.xls文件');
          
          case 422:
            if (data.details && data.details.missing_columns) {
              throw new Error(`模板缺少必需列：${data.details.missing_columns.join(', ')}`);
            }
            throw new Error('模板结构不正确，请检查列标题');
          
          case 500:
            throw new Error('服务器处理模板时出错，请稍后重试');
          
          default:
            throw new Error(data.error || '上传模板失败，请重试');
        }
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('上传超时，请检查网络连接或选择较小的文件');
      } else if (error.code === 'NETWORK_ERROR') {
        throw new Error('网络连接失败，请检查网络设置');
      }
      
      throw new Error('上传模板失败，请检查网络连接后重试');
    }
  }

  /**
   * Update an existing template
   */
  async updateTemplate(templateId, updateData) {
    try {
      const response = await httpClient.put(`${this.baseURL}/${templateId}`, updateData);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  /**
   * Replace template file while preserving metadata
   */
  async replaceTemplateFile(templateId, file, changeNotes = '', onProgress = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (changeNotes) {
        formData.append('change_notes', changeNotes);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      };

      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        };
      }

      const response = await httpClient.post(`${this.baseURL}/${templateId}/replace-file`, formData, config);
      return response.data;
    } catch (error) {
      console.error('Error replacing template file:', error);

      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.error || '文件格式错误，请选择有效的Excel文件');
          case 404:
            throw new Error('模板不存在');
          case 413:
            throw new Error('文件过大，请选择小于10MB的文件');
          default:
            throw new Error(data.error || '替换模板文件失败');
        }
      }

      throw new Error('替换模板文件失败，请检查网络连接');
    }
  }

  /**
   * Delete a template (soft delete by default, with option for hard delete)
   * @param {string} templateId - Template ID to delete
   * @param {boolean} hardDelete - If true, permanently delete template and files
   */
  async deleteTemplate(templateId, hardDelete = false) {
    try {
      const params = hardDelete ? { hard_delete: 'true' } : {};
      const response = await httpClient.delete(`${this.baseURL}/${templateId}`, { params });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  /**
   * Set a template as default for its entity type and language
   */
  async setDefaultTemplate(templateId) {
    try {
      const response = await httpClient.post(`${this.baseURL}/${templateId}/set-default`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error setting default template:', error);
      throw error;
    }
  }

  /**
   * Duplicate an existing template
   */
  async duplicateTemplate(templateId, duplicateData) {
    try {
      const response = await httpClient.post(`${this.baseURL}/${templateId}/duplicate`, duplicateData);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error duplicating template:', error);
      throw error;
    }
  }

  /**
   * Download a template file
   */
  async downloadTemplate(templateId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}/download`, {
        responseType: 'blob',
      });

      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `template_${templateId}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return {
        blob: response.data,
        filename: filename
      };
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  }

  /**
   * Get default templates for all entity types
   */
  async getDefaultTemplates() {
    try {
      const response = await httpClient.get(`${this.baseURL}/defaults`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error getting default templates:', error);
      throw error;
    }
  }

  /**
   * Get template usage statistics
   */
  async getTemplateStats() {
    try {
      const response = await httpClient.get(`${this.baseURL}/stats`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error getting template stats:', error);
      throw error;
    }
  }

  /**
   * Search templates
   */
  async searchTemplates(searchTerm, options = {}) {
    try {
      const searchOptions = {
        ...options,
        search: searchTerm
      };
      return await this.listTemplates(searchOptions);
    } catch (error) {
      console.error('Error searching templates:', error);
      throw error;
    }
  }

  /**
   * Helper method to download blob as file
   */
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  /**
   * Get entity type display name
   */
  getEntityDisplayName(entityType) {
    const entityNames = {
      'sales': '销售订单',
      'quotation': '报价单',
      'purchase': '采购订单',
      'inventory': '库存',
      'products': '产品',
      'warehouse_inbound': '入库单',
      'warehouse_outbound': '出库单'
    };
    return entityNames[entityType] || entityType;
  }

  /**
   * Get language display name
   */
  getLanguageDisplayName(language) {
    const languageNames = {
      'cn': '中文',
      'en': 'English'
    };
    return languageNames[language] || language;
  }

  /**
   * Validate template file
   */
  validateTemplateFile(file, templateId = '') {
    const errors = [];
    const warnings = [];
    
    if (!file) {
      errors.push('请选择模板文件');
      return { errors, warnings };
    }

    // Check file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    const hasValidMimeType = allowedTypes.includes(file.type);
    const hasValidExtension = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
    
    if (!hasValidMimeType && !hasValidExtension) {
      errors.push('只允许上传 .xlsx 或 .xls 格式的文件');
    } else if (!hasValidMimeType && hasValidExtension) {
      warnings.push('文件类型检测可能不准确，建议重新保存文件');
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size === 0) {
      errors.push('文件为空，请选择有效的模板文件');
    } else if (file.size > maxSize) {
      errors.push(`文件大小 ${this.formatFileSize(file.size)} 超过限制，最大允许 ${this.formatFileSize(maxSize)}`);
    }

    // Check file name
    if (file.name.length > 255) {
      errors.push('文件名过长，请使用更短的文件名');
    }

    // Check template ID if provided
    if (templateId) {
      const templateIdErrors = this.validateTemplateId(templateId);
      errors.push(...templateIdErrors);
    }

    // Additional warnings for better user experience
    if (file.size > 5 * 1024 * 1024) {
      warnings.push('文件较大，上传可能需要更长时间');
    }

    if (file.name.includes(' ')) {
      warnings.push('建议使用不含空格的文件名');
    }

    return { errors, warnings };
  }

  /**
   * Validate template ID format
   */
  validateTemplateId(templateId) {
    const errors = [];
    
    if (!templateId || !templateId.trim()) {
      errors.push('模板ID不能为空');
      return errors;
    }
    
    const trimmedId = templateId.trim();
    
    // Check length
    if (trimmedId.length < 3) {
      errors.push('模板ID至少需要3个字符');
    } else if (trimmedId.length > 50) {
      errors.push('模板ID不能超过50个字符');
    }
    
    // Check format (alphanumeric, underscores, hyphens only)
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmedId)) {
      errors.push('模板ID只能包含字母、数字、下划线和连字符');
    }
    
    // Check for reserved names
    const reservedNames = ['default', 'template', 'system', 'admin', 'test'];
    if (reservedNames.includes(trimmedId.toLowerCase())) {
      errors.push(`'${trimmedId}' 是保留名称，不能使用`);
    }
    
    return errors;
  }

  /**
   * Upload template with retry logic
   */
  async uploadTemplateWithRetry(file, metadata, onProgress = null, maxRetries = 3) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 1) {
          // Add delay between retries (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 2), 10000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        return await this.uploadTemplate(file, metadata, onProgress);
      } catch (error) {
        lastError = error;
        
        // Don't retry for certain error types
        if (error.message.includes('模板ID已存在') ||
            error.message.includes('不支持的文件格式') ||
            error.message.includes('文件过大') ||
            error.message.includes('模板验证失败')) {
          throw error;
        }
        
        // Only retry on network/server errors
        if (attempt === maxRetries) {
          throw new Error(`上传失败 (已重试 ${maxRetries} 次)：${lastError.message}`);
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Batch validate template files
   */
  validateMultipleTemplates(files, templateIds = []) {
    const results = [];
    
    files.forEach((file, index) => {
      const templateId = templateIds[index] || '';
      const validation = this.validateTemplateFile(file, templateId);
      
      results.push({
        file,
        templateId,
        ...validation,
        isValid: validation.errors.length === 0
      });
    });
    
    return results;
  }

  /**
   * Get validation summary
   */
  getValidationSummary(validationResults) {
    const summary = {
      totalFiles: validationResults.length,
      validFiles: validationResults.filter(r => r.isValid).length,
      invalidFiles: validationResults.filter(r => !r.isValid).length,
      totalErrors: validationResults.reduce((sum, r) => sum + r.errors.length, 0),
      totalWarnings: validationResults.reduce((sum, r) => sum + r.warnings.length, 0)
    };
    
    return summary;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Check if template file is corrupted or has issues
   */
  async validateTemplateIntegrity(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        try {
          const data = reader.result;
          
          // Basic file integrity checks
          const checks = {
            hasContent: data && data.byteLength > 0,
            hasValidHeader: false,
            isNotCorrupted: true
          };
          
          // Check for Excel file headers
          const uint8Array = new Uint8Array(data);
          
          if (file.name.toLowerCase().endsWith('.xlsx')) {
            // XLSX files start with PK (ZIP header)
            checks.hasValidHeader = uint8Array[0] === 0x50 && uint8Array[1] === 0x4B;
          } else if (file.name.toLowerCase().endsWith('.xls')) {
            // XLS files have specific OLE header
            checks.hasValidHeader = uint8Array[0] === 0xD0 && uint8Array[1] === 0xCF;
          }
          
          resolve(checks);
        } catch (error) {
          resolve({
            hasContent: false,
            hasValidHeader: false,
            isNotCorrupted: false,
            error: error.message
          });
        }
      };
      
      reader.onerror = () => {
        resolve({
          hasContent: false,
          hasValidHeader: false,
          isNotCorrupted: false,
          error: 'Failed to read file'
        });
      };
      
      reader.readAsArrayBuffer(file.slice(0, 512)); // Read first 512 bytes for header check
    });
  }

  // Template Versioning Methods

  /**
   * Get all versions of a template
   */
  async getTemplateVersions(templateId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}/versions`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error getting template versions:', error);
      throw error;
    }
  }

  /**
   * Create a new version of a template
   */
  async createTemplateVersion(templateId, file, versionData, onProgress = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add version metadata
      if (versionData.description) {
        formData.append('description', versionData.description);
      }
      if (versionData.change_notes) {
        formData.append('change_notes', versionData.change_notes);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      };

      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        };
      }

      const response = await httpClient.post(`${this.baseURL}/${templateId}/versions`, formData, config);
      return response.data;
    } catch (error) {
      console.error('Error creating template version:', error);
      
      if (error.response && error.response.data) {
        const { status, data } = error.response;
        
        switch (status) {
          case 400:
            if (data.error.includes('identical')) {
              throw new Error('文件与现有版本相同，无需创建新版本');
            }
            throw new Error(data.error || '创建模板版本失败，请检查文件格式');
          case 413:
            throw new Error('文件过大，请选择小于10MB的文件');
          default:
            throw new Error(data.error || '创建模板版本失败，请重试');
        }
      }
      
      throw new Error('创建模板版本失败，请检查网络连接');
    }
  }

  /**
   * Get a specific version of a template
   */
  async getTemplateVersion(templateId, versionId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}/versions/${versionId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting template version:', error);
      throw error;
    }
  }

  /**
   * Download a specific version of a template
   */
  async downloadTemplateVersion(templateId, versionId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}/versions/${versionId}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `${templateId}_version_${versionId}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return {
        blob: response.data,
        filename: filename
      };
    } catch (error) {
      console.error('Error downloading template version:', error);
      throw error;
    }
  }

  /**
   * Activate a specific version of a template
   */
  async activateTemplateVersion(templateId, versionId) {
    try {
      const response = await httpClient.post(`${this.baseURL}/${templateId}/versions/${versionId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating template version:', error);
      
      if (error.response && error.response.data) {
        throw new Error(error.response.data.error || '激活模板版本失败');
      }
      
      throw new Error('激活模板版本失败，请重试');
    }
  }

  /**
   * Delete a specific version of a template
   */
  async deleteTemplateVersion(templateId, versionId) {
    try {
      const response = await httpClient.delete(`${this.baseURL}/${templateId}/versions/${versionId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting template version:', error);
      
      if (error.response && error.response.data) {
        const { data } = error.response;
        if (data.error.includes('active version')) {
          throw new Error('无法删除活跃版本，请先激活其他版本');
        }
        throw new Error(data.error || '删除模板版本失败');
      }
      
      throw new Error('删除模板版本失败，请重试');
    }
  }

  /**
   * Get the active version of a template
   */
  async getActiveTemplateVersion(templateId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}/versions/active`);
      return response.data;
    } catch (error) {
      console.error('Error getting active template version:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive statistics for a template
   */
  async getTemplateStatistics(templateId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}/statistics`);
      return response.data;
    } catch (error) {
      console.error('Error getting template statistics:', error);
      throw error;
    }
  }

  /**
   * Clean up orphaned template version files (admin only)
   */
  async cleanupTemplateVersions() {
    try {
      const response = await httpClient.post(`${this.baseURL}/cleanup`);
      return response.data;
    } catch (error) {
      console.error('Error cleaning up template versions:', error);
      throw error;
    }
  }

  /**
   * Compare two template versions (client-side helper)
   */
  compareVersions(version1, version2) {
    const v1Parts = version1.version_number.split('.').map(Number);
    const v2Parts = version2.version_number.split('.').map(Number);
    
    const maxLen = Math.max(v1Parts.length, v2Parts.length);
    
    for (let i = 0; i < maxLen; i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;
      
      if (v1Part < v2Part) return -1;
      if (v1Part > v2Part) return 1;
    }
    
    return 0;
  }

  /**
   * Get version change summary
   */
  getVersionChangeSummary(versions) {
    if (!versions || versions.length === 0) return null;
    
    const sortedVersions = versions.sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
    
    const latest = sortedVersions[0];
    const contributors = new Set(versions.map(v => v.created_by));
    const totalSize = versions.reduce((sum, v) => sum + (v.file_size || 0), 0);
    
    return {
      latestVersion: latest,
      totalVersions: versions.length,
      contributors: Array.from(contributors),
      totalSize: totalSize,
      createdRange: {
        oldest: sortedVersions[sortedVersions.length - 1]?.created_at,
        newest: latest?.created_at
      }
    };
  }

  // Template Metadata APIs

  /**
   * Get template metadata
   */
  async getTemplateMetadata(templateId) {
    try {
      const response = await httpClient.get(`${this.baseURL}/${templateId}/metadata`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error getting template metadata:', error);
      throw error;
    }
  }

  /**
   * Update template metadata
   */
  async updateTemplateMetadata(templateId, metadata) {
    try {
      const response = await httpClient.put(
        `${this.baseURL}/${templateId}/metadata`,
        metadata
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error updating template metadata:', error);
      throw error;
    }
  }

  /**
   * Auto-detect template structure
   */
  async detectTemplateStructure(templateId) {
    try {
      const response = await httpClient.post(
        `${this.baseURL}/${templateId}/detect-structure`
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error detecting template structure:', error);
      throw error;
    }
  }

  /**
   * Preview template with metadata and sample data
   */
  async previewTemplateWithMetadata(templateId, metadata, sampleData) {
    try {
      const response = await httpClient.post(
        `${this.baseURL}/${templateId}/preview-with-metadata`,
        {
          metadata,
          sample_data: sampleData
        },
        {
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `preview_template_${templateId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error previewing template:', error);
      throw error;
    }
  }

  /**
   * Embed metadata in template file
   */
  async embedMetadataInTemplate(templateId, metadata) {
    try {
      const response = await httpClient.post(
        `${this.baseURL}/${templateId}/embed-metadata`,
        { metadata }
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error embedding metadata:', error);
      throw error;
    }
  }

  /**
   * Export metadata as JSON
   */
  exportMetadataAsJSON(metadata, templateName) {
    const json = JSON.stringify(metadata, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `template_metadata_${templateName || 'export'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Import metadata from JSON file
   */
  async importMetadataFromJSON(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const metadata = JSON.parse(e.target.result);
          resolve(metadata);
        } catch (error) {
          reject(new Error('Invalid JSON file'));
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  // Manual Metadata Management APIs

  /**
   * Update template with manual MongoDB path mappings
   */
  async updateManualMetadata(templateId, metadata) {
    try {
      const response = await httpClient.put(
        `${this.baseURL}/${templateId}/manual-metadata`,
        metadata
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error updating manual metadata:', error);
      throw error;
    }
  }

  /**
   * Validate manual metadata structure without saving
   */
  async validateManualMetadata(templateId, metadata) {
    try {
      const response = await httpClient.post(
        `${this.baseURL}/${templateId}/validate-manual-metadata`,
        metadata
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error validating manual metadata:', error);
      throw error;
    }
  }
}

const templateService = new TemplateService();
export default templateService;
